import { useEffect } from 'react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { ADS_CONVERSION_LABELS } from '#app/analytics/ads-conversions.ts'
import {
	clearEnquiryConversionMarker,
	readEnquiryConversionMarker,
} from '#app/analytics/enquiry-conversion.ts'
import { fireAdsConversion } from '#app/analytics/google-ads.ts'
import {
	clearSignupConversionMarker,
	hasSignupConversionMarker,
} from '#app/analytics/signup-conversion.ts'
import { type Route } from './+types/root.ts'
import {
	initAnalyticsClient,
	identifyClient,
	resetClient,
	trackClient,
} from './analytics/index.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { ConfettiComponent } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Competitor Backlinks' : 'Error | Competitor Backlinks' },
		{ name: 'description', content: `Competitor Backlinks.` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							email: true,
							billingAddress: {
								select: {
									country: true,
									state: true,
									postalCode: true,
								},
							},
							//image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							subscriptions: {
								select: {
									id: true,
									status: true,
									currentPeriodEnd: true,
									cancelAtPeriodEnd: true,
									plan: {
										select: {
											name: true,
											credits: true,
										},
									},
								},
								where: {
									status: {
										in: ['active', 'trialing', 'past_due'], // Only active-ish subscriptions
									},
								},
								orderBy: {
									currentPeriodEnd: 'desc', // Most recent first
								},
								take: 1, // Only need the most recent active subscription
							},
							userCredits: {
								select: {
									availableCredits: true,
									totalCredits: true,
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = await honeypot.getInputProps()

	const generalSettings = await time(
		() =>
			prisma.generalSettings.findUnique({
				where: { id: 'competitorbacklinks' },
			}),
		{
			timings,
			type: 'get general settings',
			desc: 'get general settings in root',
		},
	)

	return data(
		{
			user,
			generalSettings,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			confettiId,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{env.GOOGLE_ADS_ID ? (
					<>
						<script
							nonce={nonce}
							async
							src={`https://www.googletagmanager.com/gtag/js?id=${env.GOOGLE_ADS_ID}`}
						/>
						<script
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${env.GOOGLE_ADS_ID}', { send_page_view: false });
								`.trim(),
							}}
						/>
					</>
				) : null}
			</head>
			<body className="bg-background text-foreground data-[scroll-locked]:!overflow-visible">
				{children}
				<script
					nonce={nonce}
					suppressHydrationWarning
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()

	useToast(data.toast)

	return (
		<>
			<div className="flex min-h-screen flex-col justify-between">
				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>
			</div>
			<EpicToaster richColors position="bottom-right" theme={theme} />
			<EpicProgress />
			<PostHogClient />
			<GtagClient />
			<ConfettiComponent id={data.confettiId} />
		</>
	)
}

function PostHogClient() {
	const { user } = useLoaderData<typeof loader>()
	const location = useLocation()

	// Initialize PostHog on mount
	useEffect(() => {
		initAnalyticsClient()
	}, [])

	// Handle user identification/reset
	useEffect(() => {
		if (user?.id) {
			// Determine if user is paying based on subscription status or credits
			const activeSubscription = user.subscriptions?.[0]
			const isPaying =
				activeSubscription?.status === 'active' ||
				activeSubscription?.status === 'trialing' ||
				(user.userCredits?.availableCredits ?? 0) > 0 // Has credits = has paid

			identifyClient(user.id, {
				email: user.email ?? undefined,
				plan:
					activeSubscription?.plan?.name ||
					user.roles?.map((r) => r.name).join(',') ||
					undefined,
				is_paying: isPaying,
				credits_balance: user.userCredits?.availableCredits ?? 0,
			})
		} else {
			// No-op unless PostHog currently holds an IDENTIFIED id: an anonymous
			// visitor must keep one distinct id across the whole free funnel, which
			// spans full page loads. See resetClient.
			resetClient()
		}
	}, [
		user?.id,
		user?.email,
		user?.roles,
		user?.subscriptions,
		user?.userCredits,
	])

	// Track pageviews
	useEffect(() => {
		trackClient('$pageview', {
			route_id: location.pathname,
		})
	}, [location.key, location.pathname]) // location.key changes on every navigation

	return null
}

function GtagClient() {
	const location = useLocation()

	// Track pageviews on route changes
	useEffect(() => {
		if (!ENV.GOOGLE_ADS_ID || typeof window === 'undefined') return
		const gtag = (window as any).gtag as undefined | ((...args: any[]) => void)
		if (!gtag) return
		gtag('config', ENV.GOOGLE_ADS_ID, { page_path: location.pathname })

		// Onboarding cannot fire this itself: it redirects to a different
		// destination every time, so the signup conversion rides a one-shot
		// cookie and is claimed here, wherever the person lands. Runs on every
		// navigation rather than on mount because gtag may not have finished
		// loading on the first paint after the redirect — the marker survives
		// until a fire is confirmed.
		if (hasSignupConversionMarker()) {
			const fired = fireAdsConversion({
				label: ADS_CONVERSION_LABELS.signup,
				dedupeKey: 'signup',
			})
			if (fired) clearSignupConversionMarker()
		}

		// The service enquiry, claimed the same way and for the same reason. It
		// lands on a fixed thanks page, but that page is shareable and
		// back-navigable, so the marker — not the URL — is what says a lead is
		// real.
		const enquiryId = readEnquiryConversionMarker()
		if (enquiryId) {
			const fired = fireAdsConversion({
				label: ADS_CONVERSION_LABELS.enquiry,
				dedupeKey: `enquiry_${enquiryId}`,
			})
			if (fired) clearEnquiryConversionMarker()
		}
	}, [location.key, location.pathname]) // location.key changes on every navigation

	return null
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
